import { Show, createSignal, onCleanup, type Component } from 'solid-js';

import type { GameStore } from '../state/store';

/** How often the countdown redraws. Once a second is enough for HH:MM:SS. */
const TICK_MS = 1000;

/**
 * Milliseconds until the wipe, rendered as HH:MM:SS. Clamped at zero — a
 * negative countdown would mean the server missed its own deadline, and
 * "00:00:00" is the honest thing to show while that is sorted out.
 */
export function formatCountdown(remainingMs: number): string {
  const total = Math.max(0, Math.floor(remainingMs / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

export interface ApocalypseBannerProps {
  store: GameStore;
  /** Injectable clock, epoch milliseconds. Defaults to Date.now. */
  now?: () => number;
}

/**
 * The seasonal wipe ticker, per `doc/grind/design/ENDGAME-BOSSES.md`.
 *
 * Not rendered in Milestone 1: the server has no seasons, so
 * `store.apocalypse()` is null and this stays out of the DOM entirely.
 */
export const ApocalypseBanner: Component<ApocalypseBannerProps> = (props) => {
  const clock = () => (props.now ?? Date.now)();
  const [now, setNow] = createSignal(clock());

  const timer = setInterval(() => setNow(clock()), TICK_MS);
  onCleanup(() => clearInterval(timer));

  return (
    <Show when={props.store.apocalypse()}>
      {(state) => (
        <div class="apocalypse" data-testid="apocalypse-banner" role="status">
          <span>The Apocalypse has begun — {state().triggeredBy} has attained Level 99</span>
          <span class="apocalypse__clock" data-testid="apocalypse-clock">
            {formatCountdown(state().endsAt - now())}
          </span>
          <span>until the world wipes</span>
        </div>
      )}
    </Show>
  );
};
